import { Badge, Text } from '@thefarmersfront/kitchen';

import styles from '~/components/receive/ReceiveStatusDetailModal/ReceiveStatusDetailModal.module.scss';

import type {
  ColDef,
  GridOptions,
  ICellRendererParams,
  RowHeightParams,
  ValueFormatterParams,
} from 'ag-grid-community';

import { MultiRowCell, TableHeaderWithTooltipIcon, TextButtonCell } from '~/components/common';
import { ReceiveStatusQtyDetailCell } from '~/components/receive';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import {
  RECEIVE_STATUS_DETAIL_STATUS,
  RECEIVE_STATUS_DETAIL_STATUS_BADGE_COLOR,
  type TypeReceiveStatusDetailStatus,
} from '~/constants/receive';

import type { TypeReceiveStatusDetailItem } from '~/types/receive.types';

/**
 * [입고관리 > 입고현황] 입고 현황 상세 모달 > 입고 처리 상세 테이블
 */
const getReceiveStatusDetailModalColumnDefs = ({
  hasPermissionNFA,
  onClickViewIssueImage,
}: {
  hasPermissionNFA: boolean;
  onClickViewIssueImage: (requestItemId: number) => Promise<void>;
}) => {
  const gridOptions: GridOptions = {
    getRowHeight: (params: RowHeightParams) => {
      return params?.data?.details?.length ? params.data.details.length * 40 : 40;
    },
    columnTypes: { multiRow: { cellStyle: { padding: 0, border: 0 } } },
  };

  const columnDefs: ColDef<TypeReceiveStatusDetailItem>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'status',
      headerName: '작업상태',
      width: 92,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<TypeReceiveStatusDetailItem, TypeReceiveStatusDetailItem['status']>) => ({
        variant: 'light',
        status: true,
        color: RECEIVE_STATUS_DETAIL_STATUS_BADGE_COLOR[value as TypeReceiveStatusDetailStatus],
        children: RECEIVE_STATUS_DETAIL_STATUS[value as TypeReceiveStatusDetailStatus],
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      field: 'nsid',
      headerName: 'NSID',
      width: 180,
      hide: !hasPermissionNFA,
    },
    {
      field: 'goodsName',
      headerName: '상품명',
      width: 240,
    },
    {
      field: 'requestedQty',
      headerName: '요청수량',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReceiveStatusDetailItem, TypeReceiveStatusDetailItem['requestedQty']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'details',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        headerTitle: (
          <div className={styles['receive-status-header-qty']}>
            <Text element="span" size="large" weight="semi-bold" color="success">
              정상
            </Text>
            /
            <Text element="span" size="large" weight="semi-bold" color="error">
              문제
            </Text>
            수량
          </div>
        ),
        tooltipDescription:
          '정상수량 : 검수를 통과한 입고 수량입니다.\n문제수량 : 검수 시 발견된 문제내용으로 인해\n입고할 수 없는 수량입니다.',
      },
      width: 124,
      cellRenderer: MultiRowCell,
      cellRendererParams: { itemRenderer: ReceiveStatusQtyDetailCell },
      type: 'multiRow',
    },
    {
      field: 'details',
      headerName: '문제사유',
      width: 220,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'reason',
        itemRenderer: Text,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReceiveStatusDetailItem,
          TypeReceiveStatusDetailItem['details'][number]['reason']
        >) => ({
          size: 'large',
          children: value ?? '-',
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'details',
      headerName: '입고확정일시',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '입고확정일시에 확정된\n입고 수량만큼 재고로 전환됩니다.',
      },
      width: 160,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'receivedDate',
        itemRenderer: Text,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReceiveStatusDetailItem,
          TypeReceiveStatusDetailItem['details'][number]['receivedDate']
        >) => ({
          size: 'large',
          children: formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'hasIssueImages',
      headerName: '이슈 이미지',
      width: 92,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeReceiveStatusDetailItem>) => ({
        children: '보기',
        variant: 'normal',
        onClick: () => {
          if (!data) return;
          onClickViewIssueImage(data.requestItemId);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
  ];

  return { gridOptions, columnDefs };
};

export default getReceiveStatusDetailModalColumnDefs;
